<template>
    <div>
        <ds-multiselect
            v-model="selectedValue"
            :options="options"
            :force-invalid="forceInvalid"
            :loading="loading"
            :label="$t('global.from')"
            :placeholder="$t('global.from')"
            :submitted="submitted"
            allow-add
            :add-text="$t('email.broadcast.validated.domains.modal.title')"
            required
            searchable
            data-qa="sender-address-field-multiselect"
            @add="customizeEmailAddress"
            @input="senderSelected"
        >
            <template v-if="submitted && !selectedValue" #help>
                {{ $t('email.broadcast.validated.domains.error.fromAddressIsEmpty') }}
            </template>
        </ds-multiselect>

        <form>
            <EmailBroadcastValidatedDomainsModal
                class="validated-domains-modal"
                :environment="environment"
                :is-open="isValidatedDomainsModalOpen"
                :domains="domainOptions"
                :is-spa-app="isSpaApp"
                :show-verified-domains-new-keys-only-enabled="showVerifiedDomainsNewKeysOnlyEnabled"
                @close="() => isValidatedDomainsModalOpen = false"
                @custom-sender-selected="customSenderSelected"
            />
        </form>
    </div>
</template>

<script>
import EmailBroadcastValidatedDomainsModal from '@/components/broadcast/email/EmailBroadcastValidatedDomainsModal';
import { DEFAULT_FROM_VALUE } from '@/components/broadcast/constants/emailBroadcast.constants';
import { getEmailLabel } from '@/components/broadcast/utils/emailBroadcast.util';
import { getCustomDomains } from '@/api/broadcasts/emailBroadcast';
import { isSpaApp } from '@/api/shared';
import { getVerifiedDomainsKeysOrDmarc, getVerifiedDomainsNewKeys } from '@/components/settings/util/domains.util';

export default {
    components: {
        EmailBroadcastValidatedDomainsModal,
    },

    props: {
        value: {
            type: String,
            required: true,
        },

        forceInvalid: Boolean,

        submitted: Boolean,

        options: {
            type: Array,
            default: () => [],
        },

        environment: String,

        showVerifiedDomainsNewKeysOnlyEnabled: Boolean,
    },

    data() {
        return {
            isValidatedDomainsModalOpen: false,
            loading: true,
            selectedValue: null,
            isSpaApp: false,
            validatedDomains: [],
        };
    },

    async created() {
        this.isSpaApp = await isSpaApp();

        try {
            await this.fetchValidatedCustomDomains();
        } catch (e) {
            this.$error({
                message: this.$t('email.broadcast.validated.domains.error.failedToLoadDomains'),
                bottom: true,
            });
        } finally {
            this.loading = false;
            this.setSenderValue();
        }
    },

    watch: {
        selectedValue({ value }) {
            this.$emit('input', value);
        },

        validatedDomains() {
            this.setSenderValue();
        },
    },

    computed: {
        domainOptions() {
            return this.validatedDomains.map((domain) => {
                return {
                    label: `@${domain}`,
                    value: `@${domain}`,
                };
            });
        },
    },

    methods: {
        setSenderValue() {
            const { value } = this;
            const CONTACT_OWNER_LABEL = this.$t('unlayerBuilder.emailSender.contactOwner');

            if (!value) {
                this.selectedValue = {
                    label: CONTACT_OWNER_LABEL,
                    value: DEFAULT_FROM_VALUE,
                };

                return;
            }

            const isAppUser = this.options.some((option) => option.value === value);
            const isSenderEmailDomainAValidCustomDomain = this.validatedDomains.some((domain) => value.includes(domain));

            if (isAppUser || isSenderEmailDomainAValidCustomDomain) {
                this.selectedValue = {
                    label: value === DEFAULT_FROM_VALUE ? CONTACT_OWNER_LABEL : value,
                    value,
                };

                return;
            }

            this.$error({
                message: this.$t('email.campaignBuilder.unlayer.error.senderNotFound'),
                bottom: true,
            });
        },

        async fetchValidatedCustomDomains() {
            try {
                const customDomains = await getCustomDomains();


                if (!this.showVerifiedDomainsNewKeysOnlyEnabled) {
                    this.validatedDomains = getVerifiedDomainsKeysOrDmarc(customDomains);

                    return;
                }

                this.validatedDomains = getVerifiedDomainsNewKeys(customDomains);

                return;
            } catch (e) {
                this.$error({
                    message: this.$t('email.broadcast.editor.errors.fetchingCustomDomains'),
                    bottom: true,
                });
            }
        },

        customizeEmailAddress() {
            this.isValidatedDomainsModalOpen = true;
        },

        customSenderSelected(value) {
            const emailAddress = `${value.emailUsername}${value.selectedDomain.label}`;
            const formattedAddress = getEmailLabel({
                fullName: value.displayName,
                email: emailAddress,
            });

            this.selectedValue = {
                label: formattedAddress,
                value: formattedAddress,
            };
        },

        senderSelected({ label, value }) {
            this.selectedValue = {
                label,
                value,
            };
        },
    },
};
</script>

<style scoped>
.validated-domains-modal::v-deep .modal-content-wrapper {
    border-radius: 0.75rem;
}

.validated-domains-modal::v-deep .modal-header {
    border-radius: 0.75rem 0.75rem 0 0;
}
</style>
